import React, { PropsWithChildren, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { ChatChannel, ChatConversationItem, ChatEvent, useChatChannel, useChatEvents } from "./ChatChannel";
import { SessionUpdateEvent } from "openai/resources/beta/realtime/realtime";
import InterfaceContext, { PublicConfigurationContext } from "../Context";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { generateEmptyAudioStream } from "./RTCConnection";

type AICoachState = {
  enabled: boolean,
  active: boolean,
  hasAcceptedTerms: boolean,
  setHasAcceptedTerms: (v: AICoachState["hasAcceptedTerms"]) => void,
  modality: "text" | "audio",
  setModality: (v: AICoachState["modality"]) => void,
  audioElement: HTMLAudioElement | null,
  setAudioElement: (v: AICoachState["audioElement"]) => void,
  audioInputStream: MediaStream | null,
  transcript: ChatConversationItem[],
  startAudioInput: (stream: MediaStream) => void,
  stopAudioInput: () => void,  
  chatChannel: ChatChannel |  null
};

const defaults: AICoachState = {
  enabled: false,
  active: false,
  hasAcceptedTerms: false,
  setHasAcceptedTerms: () => {},
  modality: "text",
  setModality: () => {},
  audioElement: null,
  setAudioElement: () => {},
  audioInputStream: null,
  transcript: [],
  startAudioInput: () => {},
  stopAudioInput: () => {},
  chatChannel: null
}

export const AICoachContext = React.createContext<AICoachState>(defaults);

export function AICoachProvider({ children }: PropsWithChildren) {
  const chat = useChatChannel();
  const config = useContext(PublicConfigurationContext);
  const { lang="en" } = useContext(InterfaceContext);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<AICoachState["hasAcceptedTerms"]>(false);
  const [modality, setModality] = useState<AICoachState["modality"]>("text");
  const [audioElement, setAudioElement] = useState<AICoachState["audioElement"]>(null);
  const [audioInputStream, setAudioInputStream] = useState<MediaStream | null>(null);
  
  useEffect(() => {
    const isEnabled = !!config?.capabilities?.aiCoach && !config?.capabilities?.aiCoach?.disabled;
    chat.setEnabled(hasAcceptedTerms && isEnabled);
  }, [hasAcceptedTerms, config]);

  // if we have both an audio element and an output stream
  // bind the stream to the audio element
  useEffect(() => {
    if (audioElement && chat.outputStream) {
      audioElement.srcObject = chat.outputStream;
    }
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.srcObject = null;
      }
    }
  }, [audioElement, chat.outputStream]);

  // manage webRTC sender media stream
  // there has to be an audio media stream present on the webRTC sender
  // so if we change our input modality, call replaceTrack on the sender
  useAsyncEffect(async () => {
    // when audio stream is present, add it as the sender track
    if (audioInputStream && chat.sender) {
      await chat.sender.replaceTrack(audioInputStream.getTracks()[0]);
    }
    // when no audio stream is present, add an empty audio track
    else if(!audioInputStream && chat.sender) {
      await chat.sender.replaceTrack(generateEmptyAudioStream().getTracks()[0]);
    }
  }, [audioInputStream, chat.sender, chat.setSender]);

  // update session modality
  useEffect(() => {
    if (modality === "text") {
      stopAudioInput();
    }
    if (chat.status.status === "open") {        
      chat.updateSession({
        modalities: modality === "audio"
          ? ["text", "audio"]
          : ["text"]
      } as SessionUpdateEvent.Session);
    }
  }, [modality, chat.status.status]);

  // update language transcription
  // not sure if this is necessary since Realtime seems to handle multilingual on its own
  useEffect(() => {
    if (chat.status.status === "open" && chat.realtimeSession?.id) {
      chat.updateSession({
        input_audio_transcription: {
          ...chat.realtimeSession.input_audio_transcription,
          language: lang,
          // TODO(luke): remove `as any` once OpenAI Fixes their Types
          // `prompt` is required for update, but not for create...
          prompt: (chat.realtimeSession.input_audio_transcription as any)?.prompt ?? ""
        }
      })
    }
  }, [lang, chat.realtimeSession?.id, chat.status.status]);

  function startAudioInput(stream: MediaStream) {
    setAudioInputStream(stream);
  }

  function stopAudioInput() {
    if (audioInputStream) {
      audioInputStream.getTracks().forEach(track => track.stop());
      setAudioInputStream(null);
    }  
  }

  const stateValue: AICoachState = useMemo(() => ({
    enabled: !!config.capabilities?.aiCoach && !config.capabilities?.aiCoach?.disabled,
    active: chat.enabled && chat.status.status === 'open',
    hasAcceptedTerms, setHasAcceptedTerms,
    modality, setModality,
    audioElement, setAudioElement,
    audioInputStream,
    transcript: chat.messages,
    startAudioInput,
    stopAudioInput,
    chatChannel: chat
  }), [chat, config]);

  return <AICoachContext.Provider value={stateValue}>
    {children}
  </AICoachContext.Provider>;
}

// sugar
export function useAICoach(): AICoachState {
  const AICoach = useContext(AICoachContext);
  return AICoach;
}
